import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { expValEquals } from '@confluence/feature-experiments';
import { type UseReadFragmentInteropResult } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';

import {
	EDITOR_ONBOARDING_STATE_KEYS,
	ONBOARDING_TUTORIAL_LABEL,
} from '../../constants/onboarding-state/onboarding-editor-constants.ts';

import setOnboardingStateGQL from './__generated__/SetOnboardingStateMutation.graphql.ts';
import OnboardingEditorLabelsQuery from './__generated__/OnboardingEditorLabelsQuery.graphql.ts';
import type {
	OnboardingEditorLabelsQuery as OnboardingLabelsQueryType,
	OnboardingEditorLabelsQueryVariables,
} from './__types__/LabelsQuery.ts';
import onboardingStateGetOnboardingStateQuery from './__generated__/onboardingStateGetOnboardingStateQuery.graphql.ts';
import { deserializeState } from './onboardingState.ts';
import { writeFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/writeFragmentInterop.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';

export const setHasUserPublished = () => {
	void mutateInterop({
		mutation: setOnboardingStateGQL,
		variables: {
			key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
			value: 'true',
		},
		optimisticResponse: {
			onboardingState: [
				{
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			],
		},
		update() {
			writeFragmentInterop({
				// fragmentId follows the Apollo cache key format: `TypeName:id`.
				// OnboardingState objects are cached by their `key` field as the id
				// (see getEditorOnboardingCache where readEditorOnboardingState is called with { id: key }).
				// This mirrors how liveDocSmartCardResolver uses `Content:${contentId}`.
				fragmentId: `OnboardingState:${EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED}`,
				data: {
					key: EDITOR_ONBOARDING_STATE_KEYS.HAS_USER_PUBLISHED,
					value: 'true',
					__typename: 'OnboardingState',
				},
			});
		},
	});
};

const queryVariables = {
	key: Object.values(EDITOR_ONBOARDING_STATE_KEYS),
};

export const getEditorOnboardingQuery = (): void => {
	// When centralized onboarding state is enabled, skip this query as the centralized preloader handles it
	if (expValEquals('confluence_centralized_onboarding_state', 'isEnabled', true)) {
		return;
	}

	void queryInterop({
		query: onboardingStateGetOnboardingStateQuery,
		variables: queryVariables,
	});
};

export const getEditorOnboardingCache = (
	readEditorOnboardingState: UseReadFragmentInteropResult<any>,
) => {
	try {
		let cachedEditorOnboardingData;
		const editorKeys = Object.values(EDITOR_ONBOARDING_STATE_KEYS);

		// each onboarding item is stored with the key as the cache id
		cachedEditorOnboardingData = editorKeys.map((id) =>
			readEditorOnboardingState({
				id,
			}),
		);
		// reformat to match query shape
		cachedEditorOnboardingData = { onboardingState: cachedEditorOnboardingData };
		const state = deserializeState(cachedEditorOnboardingData);

		return state;
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'OnboardingState',
				actionSubject: 'getEditorOnboardingCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getEditorOnboardingQuery();

		// return default state on error
		return {};
	}
};

export const getLabelsQuery = (contentId: string): void => {
	void queryInterop<OnboardingLabelsQueryType, OnboardingEditorLabelsQueryVariables>({
		query: OnboardingEditorLabelsQuery,
		variables: {
			contentId,
			status: ['current', 'draft'],
		},
	});
};

export const getCachedIsOnboardingEditorLabel = (
	contentId: string,
	readLabelsFragment: UseReadFragmentInteropResult<any>,
) => {
	try {
		const labels =
			readLabelsFragment({
				id: `Content:${contentId}`,
			})?.nodes || [];

		// purposely exclude creator onboarding label as this is used for hiding the publish flag
		return (
			labels && Boolean(labels.find((label: any) => label?.label === ONBOARDING_TUTORIAL_LABEL))
		);
	} catch (cacheError) {
		// fire analytics
		void getAnalyticsWebClient().then((analyticsWebClient) => {
			analyticsWebClient.sendOperationalEvent({
				source: 'LabelsQuery',
				actionSubject: 'getEditorLabelsQueryCache',
				action: 'failed',
				attributes: {
					error: cacheError,
				},
			});
		});

		// if cache is empty then call query to load cache
		void getLabelsQuery(contentId);

		// return false on error
		return false;
	}
};
